import React, { useContext } from 'react';

import _ from 'lodash';

import styled from 'styled-components';

import { saxonize } from '@libs/NwUtils';

import { useEventsSearchContext, useEventsSearchContextDispatch } from '@contexts/EventsSearchContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import Money from '@components/Gui/Money';
import NwIcon from '@components/Gui/NwIcon';

import TransactionTitleLine from './TransactionTitleLine';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const CustomCollapsePanel = styled.div`
    border-bottom: ${(props) => props.theme.BorderInCard};

    &:last-child {
        border-bottom: none;
    }

    .custom-panel-header {
        padding: 0.5rem;
        text-transform: uppercase;
        font-size: ${(props) => props.theme.FontSizeSm};
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: space-between;
        user-select: none;
        transition: all 0.2s;

        &:hover {
            background-color: ${(props) => props.theme.LightGrayColor || 'rgba(0, 0, 0, 0.02)'};
        }

        .custom-panel-icon {
            transition: transform 0.2s;
            font-size: 10px;
            color: ${(props) => props.theme.TextMutedColor};

            &.open {
                transform: rotate(90deg);
            }
        }
    }

    .custom-panel-content {
        padding: 0.5rem;
        display: none;

        &.open {
            display: block;
        }
    }
`;

const ProjectTransactionsWrapper = styled.div`
    div.project-transactions-header {
        border-bottom: ${(props) => props.theme.DefaultBorder};
        background-color: ${(props) => props.theme.LayoutBodyBackground};
        padding: 0.4rem;
        /* min-height: 5rem; */
        margin-left: -1px;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        font-size: ${(props) => props.theme.FontSizeSm};

        h3 {
            text-transform: uppercase;
            margin-bottom: 0.25rem;
            font-size: ${(props) => props.theme.FontSizeSm};
        }
    }
    div.project-transaction-content {
        .project-transactions-content-total,
        .project-transactions-model {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            //font-size: ${(props) => props.theme.FontSizeSm};
            p {
                margin-bottom: 0;
            }
        }
    }
    .project-transactions-total-amounts {
        font-size: 0.9rem;
        font-weight: bold;
        text-align: right;
        span {
            display: block;
        }

        .project-transactions-agencyfees-line {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            span:first-child {
                font-weight: normal;
                padding-right: 0.5rem;
            }
        }
    }
`;

const ProjectTransactionsSummary = ({ modelID, project, projectId }) => {
    // Use projectId if provided, otherwise fallback to project.ID
    const currentProjectId = projectId || project?.ID;

    let contextData = null;
    let dispatch = null;
    try {
        contextData = useEventsSearchContext();
        dispatch = useEventsSearchContextDispatch();
    } catch (error) {
        // Context not available, component used outside EventsSearch
    }

    const modelTransactions = modelID ? project.Transactions.filter((item) => item.ModelID === modelID) : [];
    const hasTransactions = project.Transactions && project.Transactions.length > 0;
    const hasClientAdvances = project.Transactions.some(
        (item) => item.ClientStatements && item.ClientStatements.some((statement) => statement.isClientAdvance)
    );

    const FamilyGroups = {
        Fees: ['JobFee'],
        Expenses: ['JobExpense', 'JobRefund'],
        Other: ['Product', 'AgFee'],
        Discounts: ['JobCredit'],
    };

    const multiGroupBy = (seq, keys) => {
        if (!keys.length) return seq;
        var first = keys[0];
        var rest = keys.slice(1);
        return _.mapValues(_.groupBy(seq, first), function (value) {
            return multiGroupBy(value, rest);
        });
    };

    const getAmountsFromClientStatements = (transactions) => {
        const amounts = [];
        for (const transaction of transactions) {
            if (transaction.ClientStatements) {
                for (const statement of transaction.ClientStatements) {
                    amounts.push(statement.Amount);
                }
            } else {
                amounts.push(transaction.TotalFee);
            }
        }
        return amounts;
    };

    const getAgencyFeesFromClientStatements = (transactions) => {
        const amounts = [];
        for (const transaction of transactions) {
            if (transaction.ClientStatements) {
                for (const statement of transaction.ClientStatements) {
                    if (statement.isCommission) {
                        amounts.push(statement.Amount);
                    }
                }
            }
        }
        return amounts;
    };

    const getClientAdvancesFromClientStatements = (transactions) => {
        const amounts = [];
        for (const transaction of transactions) {
            if (transaction.ClientStatements) {
                for (const statement of transaction.ClientStatements) {
                    if (statement.isClientAdvance) {
                        amounts.push(statement.Amount);
                    }
                }
            }
        }
        return amounts;
    };

    const ProjectTransactionsTotalAmount = () => {
        if (!hasTransactions) {
            return <div className="project-transactions-total-amounts">0</div>;
        }
        const transactionsByCurrency = _.groupBy(project.Transactions, 'Currency');
        if (transactionsByCurrency.length === 0) return null;
        return (
            <div className="project-transactions-total-amounts">
                {Object.entries(transactionsByCurrency).map(([key, value]) => (
                    <Money key={key} highlightCurrency amounts={getAmountsFromClientStatements(value)} currency={key} />
                ))}
            </div>
        );
    };

    const ProjectTransactionsTotalAmountsByType = ({ type, label }) => {
        const transactionsByType = project.Transactions.filter((item) => FamilyGroups[type].includes(item.Family));
        const transactionsByCurrency = _.groupBy(transactionsByType, 'Currency');
        if (transactionsByType.length === 0) return null;
        return (
            <div className="project-transactions-content-total">
                <p>{label || type}</p>
                <div className="project-transactions-total-amounts">
                    {Object.entries(transactionsByCurrency).map(([key, value]) => {
                        if (type === 'Fees') {
                            return (
                                <>
                                    <Money
                                        key={key}
                                        highlightCurrency
                                        amounts={value.map((item) => item.TotalFee)}
                                        currency={key}
                                    />
                                    <div className="project-transactions-agencyfees-line">
                                        <span>+ Ag. Fees:</span>
                                        <Money
                                            key={key}
                                            highlightCurrency
                                            amounts={getAgencyFeesFromClientStatements(value)}
                                            currency={key}
                                        />
                                    </div>
                                    {hasClientAdvances && (
                                        <div className="project-transactions-clientadvance-line">
                                            <span>- Client Advance:</span>
                                            <Money
                                                key={key}
                                                highlightCurrency
                                                amounts={getClientAdvancesFromClientStatements(value)}
                                                currency={key}
                                            />
                                        </div>
                                    )}
                                </>
                            );
                        } else {
                            return (
                                <Money
                                    key={key}
                                    highlightCurrency
                                    amounts={value.map((item) => item.TotalFee)}
                                    currency={key}
                                />
                            );
                        }
                    })}
                </div>
            </div>
        );
    };

    const ProjectTransactionsByModel = () => {
        const transactionsByModelAndCurrency = multiGroupBy(project.Transactions, ['ModelID', 'Currency']);
        return Object.entries(transactionsByModelAndCurrency).map(([key, value]) => (
            <div key={key} className="project-transactions-model">
                <p>{key !== 'null' ? getContactName(key) : 'no model'}</p>
                <div className="project-transactions-total-amounts">
                    {Object.entries(value).map(([key2, value2]) => (
                        <Money
                            key={key2}
                            highlightCurrency
                            amounts={value2.map((item) => item.TotalFee)}
                            currency={key2}
                        />
                    ))}
                </div>
            </div>
        ));
    };

    const ProjectTransactionsModel = () => {
        if (modelTransactions.length === 0) {
            return <div className="project-transactions-total-amounts">0</div>;
        }
        const transactionsByCurrency = _.groupBy(modelTransactions, 'Currency');
        return (
            <div className="project-transactions-total-amounts">
                {Object.entries(transactionsByCurrency).map(([key, value]) => (
                    <Money key={key} highlightCurrency amounts={value.map((item) => item.TotalFee)} currency={key} />
                ))}
            </div>
        );
    };

    const ProjectTransactionsModelDetails = () => {
        return modelTransactions.map((item, index) => (
            <div key={index} className="project-transactions-model">
                <TransactionTitleLine transaction={item} inline />
                <div className="project-transactions-total-amounts">
                    <Money highlightCurrency amount={item.TotalFee} currency={item.Currency} />
                </div>
            </div>
        ));
    };

    const togglePanel = (panelKey) => {
        if (dispatch && currentProjectId) {
            dispatch({
                type: 'SetActiveTransactionCollapse',
                projectId: String(currentProjectId),
                collapseKey: panelKey,
            });
        }
    };

    const isPanelOpen = (panelKey) => {
        if (contextData && contextData.activeTransactionCollapse) {
            const currentId = String(currentProjectId);
            const keys = contextData.activeTransactionCollapse[currentId] || [];
            return keys.includes(panelKey);
        }
        return false;
    };

    if (modelID) {
        return (
            <ProjectTransactionsWrapper>
                <div className="project-transactions-header">
                    <h3>{saxonize(getContactName(modelID))} fees &amp; expenses</h3>
                    <ProjectTransactionsModel />
                </div>
                <div className="project-transaction-content">
                    {project.Transactions.length > 0 ? (
                        <>
                            {modelTransactions.length > 0 && (
                                <CustomCollapsePanel>
                                    <div className="custom-panel-header" onClick={() => togglePanel('1')}>
                                        <span>{saxonize(getContactName(modelID))} transaction details</span>
                                        <NwIcon
                                            icon={byPrefixAndName.fass['chevron-right']}
                                            className={`custom-panel-icon ${isPanelOpen('1') ? 'open' : ''}`}
                                        />
                                    </div>
                                    <div className={`custom-panel-content ${isPanelOpen('1') ? 'open' : ''}`}>
                                        <ProjectTransactionsModelDetails />
                                    </div>
                                </CustomCollapsePanel>
                            )}
                            <CustomCollapsePanel>
                                <div className="custom-panel-header" onClick={() => togglePanel('2')}>
                                    <span>Project Transactions Summary</span>
                                    <NwIcon
                                        icon={byPrefixAndName.fass['chevron-right']}
                                        className={`custom-panel-icon ${isPanelOpen('2') ? 'open' : ''}`}
                                    />
                                </div>
                                <div className={`custom-panel-content ${isPanelOpen('2') ? 'open' : ''}`}>
                                    <ProjectTransactionsTotalAmountsByType type="Fees" />
                                    <ProjectTransactionsTotalAmountsByType type="Expenses" />
                                    <ProjectTransactionsTotalAmountsByType type="Other" />
                                    <ProjectTransactionsTotalAmountsByType type="Discounts" />
                                    <div
                                        className="project-transactions-content-total"
                                        style={{ borderTop: '1px solid #000', paddingTop: '.25rem' }}
                                    >
                                        <p>
                                            <strong>Total</strong>
                                        </p>
                                        <ProjectTransactionsTotalAmount />
                                    </div>
                                </div>
                            </CustomCollapsePanel>
                        </>
                    ) : (
                        <p></p>
                    )}
                </div>
            </ProjectTransactionsWrapper>
        );
    }

    return (
        <ProjectTransactionsWrapper>
            <div className="project-transactions-header">
                <h3>fees &amp; expenses (total)</h3>
                <ProjectTransactionsTotalAmount />
            </div>
            <div className="project-transaction-content">
                {hasTransactions && (
                    <>
                        <CustomCollapsePanel>
                            <div className="custom-panel-header" onClick={() => togglePanel('1')}>
                                <span>Transactions by type</span>
                                <NwIcon
                                    icon={byPrefixAndName.fass['chevron-right']}
                                    className={`custom-panel-icon ${isPanelOpen('1') ? 'open' : ''}`}
                                />
                            </div>
                            <div className={`custom-panel-content ${isPanelOpen('1') ? 'open' : ''}`}>
                                <ProjectTransactionsTotalAmountsByType type="Fees" />
                                <ProjectTransactionsTotalAmountsByType type="Expenses" />
                                <ProjectTransactionsTotalAmountsByType type="Other" />
                                <ProjectTransactionsTotalAmountsByType type="Discounts" />
                            </div>
                        </CustomCollapsePanel>
                        <CustomCollapsePanel>
                            <div className="custom-panel-header" onClick={() => togglePanel('2')}>
                                <span>Transactions by model</span>
                                <NwIcon
                                    icon={byPrefixAndName.fass['chevron-right']}
                                    className={`custom-panel-icon ${isPanelOpen('2') ? 'open' : ''}`}
                                />
                            </div>
                            <div className={`custom-panel-content ${isPanelOpen('2') ? 'open' : ''}`}>
                                <ProjectTransactionsByModel />
                            </div>
                        </CustomCollapsePanel>
                    </>
                )}
            </div>
        </ProjectTransactionsWrapper>
    );
};

export default ProjectTransactionsSummary;
